import { action, makeObservable, observable, runInAction, computed } from 'mobx';
import {
  failureToSay,
  type HeldSentenceType,
  pageWords,
  sayFailure,
} from '@/services/actionFailure';

/**
 * IU-9 (F-0510, *Detail*, row 18/19 of *Every action and its rule*): the one toast Archive raises,
 * held outside the item page's own component tree — Archive pops back to the list (the page that
 * showed the toast's trigger unmounts in the same tick), so the toast has to live somewhere that
 * survives the navigation, the same reason `DecisionAnswerToastStore` does for *Take it back*.
 *
 * Mounted once (`ItemArchiveToast`, in `Layout.tsx`), not inside `WorkItemDetail`. A single active
 * toast: archiving a second item while one toast shows replaces it, never stacks — the same rule
 * `DecisionAnswerToastStore` follows.
 */
export interface IItemArchiveToastState {
  readonly itemId: string;
  readonly itemKey: string;
  readonly expiresAt: number;
}

/** M-8's own toast window, shared with the decision answer toast. */
export const ITEM_ARCHIVE_TOAST_MS = 8_000;

export interface IItemArchiveToastDeps {
  /**
   * `workItemStore.unarchive` — handed in rather than imported directly (`stores/index.ts` would
   * otherwise import this file, which would import `stores/index.ts` back for `workItemStore`,
   * the same circular-import shape `DecisionsStore`'s own constructor dependencies avoid).
   */
  readonly unarchive: (itemId: string) => Promise<void>;
}

export class ItemArchiveToastStore {
  toast: IItemArchiveToastState | null = null;
  /** Set once an Undo from the toast has been asked for and failed; cleared by a fresh show. */
  heldFailure: HeldSentenceType | null = null;
  get failure(): string | null {
    return sayFailure(pageWords(), this.heldFailure);
  }
  set failure(value: HeldSentenceType | null) {
    this.heldFailure = value;
  }
  undoing = false;

  private readonly deps: IItemArchiveToastDeps;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(deps: IItemArchiveToastDeps) {
    this.deps = deps;
    makeObservable(this, {
      toast: observable.ref,
      heldFailure: observable.ref,
      failure: computed,
      undoing: observable,
      show: action,
      dismiss: action,
      undo: action,
    });
  }

  show(itemId: string, itemKey: string): void {
    if (this.timer) clearTimeout(this.timer);
    this.failure = null;
    this.toast = { itemId, itemKey, expiresAt: Date.now() + ITEM_ARCHIVE_TOAST_MS };
    this.timer = setTimeout(() => runInAction(() => this.dismiss()), ITEM_ARCHIVE_TOAST_MS);
  }

  dismiss(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.toast = null;
    this.failure = null;
  }

  /**
   * *Detail*, *Archive*: "Once the server confirms, … the toast says Archived I-0016 · Undo, which
   * unarchives." A failed Undo leaves the toast open with **Retry**, the same as a failed take-back.
   */
  async undo(): Promise<void> {
    const current = this.toast;
    if (!current || this.undoing) return;
    this.undoing = true;
    this.failure = null;
    try {
      await this.deps.unarchive(current.itemId);
      runInAction(() => {
        this.undoing = false;
        this.dismiss();
      });
    } catch (error) {
      runInAction(() => {
        this.undoing = false;
        this.failure = failureToSay(error, 'workItems.actions.unarchive');
      });
    }
  }

  /** Tests only. */
  resetForTests(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.toast = null;
    this.failure = null;
    this.undoing = false;
  }
}
